import { ref, shallowRef, watch } from 'vue'

import { defineStore } from 'pinia'

import { ChatApi } from '@/apiClient/modules/chat'
import { ChatSurveyApi } from '@/apiClient/modules/chatSurvey'
import { DEFAULT_CHAT_TITLE } from '@/apiClient/queries/chatPlaceholderChat'
import type {
  IChatSurvey,
  IChatSurveyContext,
  IChatSurveyOption,
  IChatSurveyPrompt,
} from '@/apiClient/types/chatSurvey'
import { useMetricEvents } from '@/composables/useMetricEvents'
import { useAuthStore } from '@/store/auth'
import { useChatStore } from '@/store/chat'
import { redactCardNumbers } from '@/utils/cardNumberRedaction'
import {
  isSessionDismissed,
  setSessionDismissed,
} from '@/utils/sessionDismissal'

const answeredKeyFor = (userId: string) =>
  `evergpt-chat-survey-answered:${userId}`

const dismissedKeyFor = (userId: string) =>
  `evergpt-chat-survey-dismissed:${userId}`

const lastShownKeyFor = (userId: string) =>
  `evergpt-chat-survey-last-shown:${userId}`

const openPromptsKeyFor = (userId: string) =>
  `evergpt-chat-survey-open:${userId}`

const readJsonList = <T>(read: () => string | null): T[] => {
  try {
    const list = JSON.parse(read() ?? '[]')
    return Array.isArray(list) ? list : []
  } catch {
    // Private mode / sandboxed iframe / a value that isn't a list.
    return []
  }
}

/** What one showing covers: the whole publish, or one chat within it. */
const askIdFor = (
  { published_at, display_frequency }: IChatSurvey,
  chatId: string,
) =>
  display_frequency === 'once_per_chat'
    ? `${published_at}:${chatId}`
    : `${published_at}`

const sessionViewIdFor = (askId: string) => `chat-survey:${askId}`

/** The asks answered or closed in this browser, for one publish. */
const readAsksForPublish = (storageKey: string, survey: IChatSurvey) =>
  readJsonList<string>(() => localStorage.getItem(storageKey)).filter((askId) =>
    askId.startsWith(`${survey.published_at}`),
  )

const recordAsk = (storageKey: string, survey: IChatSurvey, chatId: string) => {
  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify([
        ...readAsksForPublish(storageKey, survey),
        askIdFor(survey, chatId),
      ]),
    )
  } catch {
    // Storage unavailable: the user is asked again next session.
  }
}

const hasReachedDismissalLimit = (userId: string, survey: IChatSurvey) =>
  survey.max_dismissals > 0 &&
  readAsksForPublish(dismissedKeyFor(userId), survey).length >=
    survey.max_dismissals

const readLastShown = (userId: string) => {
  try {
    return JSON.parse(localStorage.getItem(lastShownKeyFor(userId)) ?? 'null')
  } catch {
    return null
  }
}

const writeLastShown = (userId: string, survey: IChatSurvey) => {
  try {
    localStorage.setItem(
      lastShownKeyFor(userId),
      JSON.stringify({
        published_at: survey.published_at,
        shown_at: Date.now(),
      }),
    )
  } catch {
    // Storage unavailable: the cooldown doesn't apply.
  }
}

/** A republish asks everyone again, so only a card from this publish counts. */
const isInChatCooldown = (userId: string, survey: IChatSurvey) => {
  if (survey.display_frequency !== 'once_per_chat') return false
  const lastShown = readLastShown(userId)
  return (
    lastShown?.published_at === survey.published_at &&
    Date.now() - lastShown.shown_at < survey.chat_cooldown_hours * 3_600_000
  )
}

const canAskSurvey = (survey: IChatSurvey, chatId: string) => {
  const userId = useAuthStore().user?.id
  if (!userId || !survey.is_live || !survey.published_at) return false
  const askId = askIdFor(survey, chatId)
  return (
    !readAsksForPublish(answeredKeyFor(userId), survey).includes(askId) &&
    !isSessionDismissed(sessionViewIdFor(askId), userId) &&
    !hasReachedDismissalLimit(userId, survey) &&
    !isInChatCooldown(userId, survey)
  )
}

const isGeneratedTitle = (title: string | undefined): title is string =>
  !!title && title !== DEFAULT_CHAT_TITLE

/** The chat's name right now, or null while it has only the default title. */
const readChatTitle = async (chatId: string) => {
  const loadedTitle = useChatStore().getChatById(chatId)?.title
  if (isGeneratedTitle(loadedTitle)) return redactCardNumbers(loadedTitle)
  try {
    // Not loaded after a reload, or this tab missed the `new_title` event.
    const { title } = await ChatApi.get(chatId, { include_messages: false })
    return isGeneratedTitle(title) ? redactCardNumbers(title) : null
  } catch {
    return null
  }
}

export const useChatSurveyStore = defineStore('chatSurvey', () => {
  /** The finished reply the wait counts from, and the survey to ask after it. */
  const pendingPrompt = ref<{
    survey: IChatSurvey
    chatId: string
    messageId: string
  } | null>(null)
  /** The open cards: one that follows the user across chats, or one per chat. */
  const openPrompts = shallowRef<IChatSurveyPrompt[]>([])

  const setOpenPrompts = (prompts: IChatSurveyPrompt[]) => {
    openPrompts.value = prompts
    const userId = useAuthStore().user?.id
    if (!userId) return
    try {
      sessionStorage.setItem(openPromptsKeyFor(userId), JSON.stringify(prompts))
    } catch {
      // Storage unavailable: a reload drops the card until next session.
    }
  }

  const findOpenPrompt = (chatId: string) =>
    openPrompts.value.find(
      ({ survey, context }) =>
        survey.display_frequency !== 'once_per_chat' ||
        context.chat_id === chatId,
    ) ?? null

  const closePrompt = (prompt: IChatSurveyPrompt) => {
    setOpenPrompts(openPrompts.value.filter((open) => open !== prompt))
  }

  const closeOutdatedPrompts = (survey: IChatSurvey | null) => {
    const current = openPrompts.value.filter(
      (open) =>
        survey?.is_live && open.survey.published_at === survey.published_at,
    )
    if (current.length !== openPrompts.value.length) setOpenPrompts(current)
  }

  const sendPromptEvent = (
    name: string,
    { survey, context }: IChatSurveyPrompt,
    answer = {},
  ) => {
    useMetricEvents().sendEvent(name, {
      survey_key: survey.key,
      survey_published_at: survey.published_at,
      question: survey.question,
      employee_id: useAuthStore().user?.employee_id,
      ...context,
      chat_type: context.gpt_id ? 'gpt' : 'regular',
      ...answer,
    })
  }

  let latestReplyRead = 0

  /** Every reply reads the survey fresh, so publishes reach open sessions without polling. */
  const onReplyCompleted = async (chatId: string, messageId: string) => {
    if (!useAuthStore().user?.id) return
    pendingPrompt.value = null
    latestReplyRead += 1
    const replyRead = latestReplyRead

    let survey: IChatSurvey | null
    try {
      survey = await ChatSurveyApi.get()
    } catch {
      return
    }
    if (replyRead !== latestReplyRead) return

    closeOutdatedPrompts(survey)

    if (survey && canAskSurvey(survey, chatId)) {
      pendingPrompt.value = { survey, chatId, messageId }
    }
  }

  const showPrompt = (context: IChatSurveyContext) => {
    const pending = pendingPrompt.value
    pendingPrompt.value = null
    const userId = useAuthStore().user?.id
    if (!pending || !userId || !canAskSurvey(pending.survey, pending.chatId)) {
      return
    }
    setSessionDismissed(
      sessionViewIdFor(askIdFor(pending.survey, pending.chatId)),
      userId,
    )
    writeLastShown(userId, pending.survey)
    const prompt = { survey: pending.survey, context }
    setOpenPrompts([...openPrompts.value, prompt])
    sendPromptEvent('chat_survey_shown', prompt)
  }

  const answerPrompt = async (
    prompt: IChatSurveyPrompt,
    { id, label }: IChatSurveyOption,
  ) => {
    const userId = useAuthStore().user?.id
    if (userId) {
      recordAsk(answeredKeyFor(userId), prompt.survey, prompt.context.chat_id)
    }
    closePrompt(prompt)
    sendPromptEvent('chat_survey_answered', prompt, {
      chat_title: await readChatTitle(prompt.context.chat_id),
      option_id: id,
      option_label: label,
    })
  }

  const dismissPrompt = (prompt: IChatSurveyPrompt) => {
    const userId = useAuthStore().user?.id
    if (userId) {
      recordAsk(dismissedKeyFor(userId), prompt.survey, prompt.context.chat_id)
    }
    sendPromptEvent('chat_survey_dismissed', prompt)
    closePrompt(prompt)
  }

  // A reload keeps sessionStorage, so an open card comes back unless the survey moved on.
  watch(
    () => useAuthStore().user?.id,
    (userId) => {
      if (!userId) return
      openPrompts.value = readJsonList<IChatSurveyPrompt>(() =>
        sessionStorage.getItem(openPromptsKeyFor(userId)),
      )
      if (!openPrompts.value.length) return
      ChatSurveyApi.get()
        .then(closeOutdatedPrompts)
        .catch(() => {
          // Couldn't check: the next reply reads the survey again.
        })
    },
    { immediate: true },
  )

  return {
    pendingPrompt,
    openPrompts,
    findOpenPrompt,
    onReplyCompleted,
    showPrompt,
    answerPrompt,
    dismissPrompt,
  }
})
